import React, { useState, useEffect } from 'react'
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  DateField,
  BooleanField,
  FunctionField,
  TopToolbar,
  useRefresh,
  TextInput,
  Filter,
  SimpleList
} from 'react-admin'
import { Box, Typography, Chip, Switch, FormControlLabel, Tabs, Tab, TextField as MuiTextField, InputAdornment, useMediaQuery, useTheme } from '@mui/material'
import { Home, LocalParking, Search } from '@mui/icons-material'
import { useInvestmentContext } from '../contexts/InvestmentContext'
import { supabase } from '../supabaseClient'
import { Property } from '../types'
import { getInvestmentPropertiesAtDate } from '@repo/supabase'
import { PriceColumn } from './PriceColumn'
import { StatusColumn } from './StatusColumn'
import { PropertyCard } from './PropertyCard'
import { PropertyCardList } from './PropertyCardList'
import { PropertyGridList } from './PropertyGridList'
import { PromoBadgeManagerDialog } from './PromoBadgeManagerDialog'
import { usePromoBadges } from '../hooks/usePromoBadges'

const PropertyListActions = ({
  showSold,
  setShowSold,
  isMobile,
  searchQuery,
  setSearchQuery
}: {
  showSold: boolean,
  setShowSold: (value: boolean) => void,
  isMobile: boolean,
  searchQuery: string,
  setSearchQuery: (value: string) => void
}) => (
  <TopToolbar>
    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={1} sx={{ px: { xs: 2, sm: 0 } }}>
      {isMobile && (
        <MuiTextField
          label="Szukaj po nazwie"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          variant="outlined"
          sx={{
            minWidth: 200,
            flexGrow: 1,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'background.paper',
              borderRadius: 1,
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      )}
      <FormControlLabel
        control={
          <Switch
            checked={showSold}
            onChange={(e) => setShowSold(e.target.checked)}
            size="medium"
          />
        }
        label="Pokaż sprzedane"
        sx={{ flexShrink: 0 }}
      />
    </Box>
  </TopToolbar>
)

const PropertyListFilters = () => (
  <Filter>
    <TextInput
      label="Szukaj po nazwie"
      source="title_ilike"
      alwaysOn
      sx={{ minWidth: 250, ml: 2 }}
    />
  </Filter>
)


// Helper function to create property type label
const getPropertyTypeLabel = (key: string): string => {
  const labels: { [key: string]: string } = {
    'lokal-mieszkalny': 'Lokale mieszkalne',
    'miejsce-postojowe': 'Miejsca postojowe',
    'niezadaszone-miejsce-wylacznego-korzystania': 'Niezadaszone miejsca do wyłącznego korzystania',
    'house': 'Domy'
  }
  return labels[key] || key // Fallback to key if no label defined
}

// Helper function to get icon for property type
const getPropertyTypeIcon = (key: string) => {
  switch(key) {
    case 'lokal-mieszkalny':
    case 'house':
      return Home
    case 'miejsce-postojowe':
      return LocalParking
    default:
      return Home // Default icon
  }
}

export function PropertyList() {
  const { currentInvestment, loading } = useInvestmentContext()
  const [showSold, setShowSold] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [availableTypes, setAvailableTypes] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [kafelkiDialogOpen, setKafelkiDialogOpen] = useState(false)
  const { badges: promoBadges, moduleEnabled: promoModuleEnabled, reload: reloadPromoBadges } = usePromoBadges()
  const refresh = useRefresh()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Załaduj dostępne typy właściwości dla bieżącej inwestycji
  useEffect(() => {
    if (!currentInvestment?.slug) return

    const loadAvailableTypes = async () => {
      try {
        // Use RPC helper to get all properties for this investment
        const data = await getInvestmentPropertiesAtDate(
          supabase,
          currentInvestment.slug,
          new Date()
        )

        const types = [...new Set(data.map(p => p.property_type).filter(Boolean))]
        setAvailableTypes(types)
        console.log('📝 Available property types:', types)
        
        // Reset active tab if it's out of bounds
        if (activeTab >= types.length && types.length > 0) {
          setActiveTab(0)
        }
      } catch (error) {
        console.error('Error loading available types:', error)
      }
    }

    loadAvailableTypes()
  }, [currentInvestment?.slug])

  // Odśwież listę gdy zmieni się inwestycja
  useEffect(() => {
    if (currentInvestment) {
      console.log('🔄 Investment changed, refreshing list:', currentInvestment.name)
      refresh()
    }
  }, [currentInvestment?.id, refresh])

  if (loading) {
    return <div>Ładowanie...</div>
  }

  if (!currentInvestment) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="text.secondary">
          Wybierz inwestycję z menu aby wyświetlić nieruchomości
        </Typography>
      </Box>
    )
  }

  // Create property types dynamically from available types
  const availablePropertyTypes = availableTypes.map(typeKey => ({
    key: typeKey,
    label: getPropertyTypeLabel(typeKey),
    icon: getPropertyTypeIcon(typeKey)
  }))
  
  // Jeśli nie ma dostępnych typów, pokaż komunikat
  if (availableTypes.length === 0) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="text.secondary">
          Brak nieruchomości w wybranej inwestycji
        </Typography>
      </Box>
    )
  }

  const currentPropertyType = availablePropertyTypes[activeTab] || availablePropertyTypes[0]

  // If no property type is available, show message
  if (!currentPropertyType) {
    return (
      <Box p={3}>
        <Typography variant="h6" color="text.secondary">
          Brak dostępnych typów nieruchomości dla tej inwestycji
        </Typography>
      </Box>
    )
  }

  // Filter properties by current investment slug and property type
  const filter: any = {
    'investment_slug': currentInvestment.slug,
    'property_type': currentPropertyType.key
  }

  // Add status filter based on showSold switch
  if (!showSold) {
    filter.status_codes = ['available', 'reserved']
  }

  // Add search filter if mobile and search query exists
  if (isMobile && searchQuery.trim()) {
    filter.title_ilike = `%${searchQuery.trim()}%`
  }

  console.log('🎯 PropertyList filter:', JSON.stringify(filter, null, 2))
  console.log('📊 Current investment:', currentInvestment.name, `(${currentInvestment.slug})`)
  console.log('📝 Current property type:', currentPropertyType.key)
  console.log('📋 Available types:', availableTypes)
  console.log('📱 Is mobile:', isMobile, 'Show sold:', showSold)

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ px: 3 }}
        >
          {availablePropertyTypes.map((type, index) => {
            const IconComponent = type.icon
            return (
              <Tab
                key={type.key}
                label={type.label}
                icon={<IconComponent />}
                iconPosition="start"
              />
            )
          })}
        </Tabs>
      </Box>

      <List
        key={`${currentInvestment.id}-${currentPropertyType.key}-${showSold}`}
        perPage={1000}
        actions={
          <PropertyListActions
            showSold={showSold}
            setShowSold={setShowSold}
            isMobile={isMobile}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
        }
        filters={!isMobile ? <PropertyListFilters /> : undefined}
        filter={filter}
        title={`${currentPropertyType.label} - ${currentInvestment.name}`}
        resource="properties"
        pagination={false}
        sort={{ field: 'title', order: 'DESC' }}
      >
        <PropertyGridList
          promoBadges={promoBadges}
          promoModuleEnabled={promoModuleEnabled}
          onManageKafelki={() => setKafelkiDialogOpen(true)}
        />
      </List>

      <PromoBadgeManagerDialog
        open={kafelkiDialogOpen}
        onClose={() => setKafelkiDialogOpen(false)}
        onChanged={() => { reloadPromoBadges(); refresh() }}
      />
    </Box>
  )
}